import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { listCoaches, createCoach } from "@/lib/coaches.functions";
import type { CoachRow } from "@/lib/coaches.types";
import { INDUSTRIES, FUNCTIONS, PHASES } from "@/lib/coach-options";
import { toast } from "sonner";
import { Copy, UserPlus, Search, Wand2 } from "lucide-react";

export const Route = createFileRoute("/coacher/")({
  loader: () => listCoaches(),
  component: CoacherPage,
  errorComponent: ({ error }) => (
    <AppShell><div className="p-8 text-sm text-destructive">{error.message}</div></AppShell>
  ),
});

function CoacherPage() {
  const { coaches } = Route.useLoaderData();
  const router = useRouter();
  const create = useServerFn(createCoach);

  const [q, setQ] = useState("");
  const [industries, setIndustries] = useState<string[]>([]);
  const [functions, setFunctions] = useState<string[]>([]);
  const [phases, setPhases] = useState<string[]>([]);
  const [onlyActive, setOnlyActive] = useState(true);

  const [view, setView] = useState<"grid" | "list">("list");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [createdLink, setCreatedLink] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return coaches.filter((c: CoachRow) => {
      if (onlyActive && !c.active) return false;
      if (term && !`${c.full_name} ${c.email} ${c.city ?? ""} ${c.bio ?? ""}`.toLowerCase().includes(term)) return false;
      if (industries.length && !industries.every((i) => c.industries.includes(i))) return false;
      if (functions.length && !functions.every((f) => c.functions.includes(f))) return false;
      if (phases.length && !phases.every((p) => c.business_phases.includes(p))) return false;
      return true;
    });
  }, [coaches, q, industries, functions, phases, onlyActive]);

  async function handleCreate() {
    if (!name.trim() || !email.trim()) {
      toast.error("Fyll i namn och e-post.");
      return;
    }
    try {
      const res = await create({ data: { full_name: name.trim(), email: email.trim() } });
      const link = `${window.location.origin}/coach-formular/${res.token}`;
      setCreatedLink(link);
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte skapa coach.");
    }
  }

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-16 max-w-7xl">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Affärscoacher</div>
            <h1 className="font-display text-4xl">Kompetensdatabas & matchning</h1>
            <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
              Sök och filtrera coacher på bransch, funktion och affärsfas. Bjud in nya coacher med en länk de fyller i själva.
            </p>
          </div>

          <div className="flex gap-2">
            <Link to="/coach-matchning"><Button variant="outline"><Wand2 className="size-4 mr-2" />AI-matchning</Button></Link>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setCreatedLink(null); setName(""); setEmail(""); } }}>
            <DialogTrigger asChild>
              <Button><UserPlus className="size-4 mr-2" />Bjud in coach</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{createdLink ? "Länk skapad" : "Bjud in ny coach"}</DialogTitle></DialogHeader>
              {createdLink ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Skicka denna länk till coachen via e-post. Den är giltig i 60 dagar.</p>
                  <div className="flex gap-2">
                    <Input readOnly value={createdLink} onFocus={(e) => e.currentTarget.select()} />
                    <Button variant="secondary" onClick={() => { navigator.clipboard.writeText(createdLink); toast.success("Kopierat"); }}>
                      <Copy className="size-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div><Label>Namn</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Anna Andersson" /></div>
                  <div><Label>E-post</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="anna@exempel.se" /></div>
                </div>
              )}
              <DialogFooter>
                {createdLink ? (
                  <Button onClick={() => setOpen(false)}>Klart</Button>
                ) : (
                  <Button onClick={handleCreate}>Skapa länk</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Sök på namn, e-post, ort, bio…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <FilterGroup label="Branscher" options={INDUSTRIES} value={industries} onChange={setIndustries} />
          <FilterGroup label="Funktionella områden" options={FUNCTIONS} value={functions} onChange={setFunctions} />
          <FilterGroup label="Affärsfaser" options={PHASES} value={phases} onChange={setPhases} />
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked={onlyActive} onCheckedChange={(v) => setOnlyActive(!!v)} />
            Visa endast aktiva coacher
          </label>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-muted-foreground">{filtered.length} av {coaches.length} coacher</div>
          <div className="flex gap-1 rounded-full border border-border p-0.5 text-xs">
            <button type="button" onClick={() => setView("list")} className={`px-3 py-1 rounded-full transition-colors ${view === "list" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>Lista</button>
            <button type="button" onClick={() => setView("grid")} className={`px-3 py-1 rounded-full transition-colors ${view === "grid" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>Kort</button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            Inga coacher matchar filtret. {coaches.length === 0 && "Bjud in den första coachen för att komma igång."}
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((c: CoachRow) => <CoachCard key={c.id} c={c} />)}
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card max-h-[calc(100vh-22rem)] overflow-y-auto divide-y divide-border">
            {filtered.map((c: CoachRow) => <CoachListRow key={c.id} c={c} />)}
          </div>
        )}
      </section>
    </AppShell>
  );
}

function FilterGroup({ label, options, value, onChange }: { label: string; options: readonly string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(active ? value.filter((v) => v !== o) : [...value, o])}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}
            >{o}</button>
          );
        })}
      </div>
    </div>
  );
}

function CoachCard({ c }: { c: CoachRow }) {
  const initials = c.full_name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <Link to="/coacher/$id" params={{ id: c.id }} className="rounded-2xl border border-border bg-card p-5 hover:border-foreground/30 transition-colors block">
      <div className="flex items-start gap-3 mb-3">
        {c.photo_url ? (
          <img src={c.photo_url} alt={c.full_name} className="size-12 rounded-full object-cover" />
        ) : (
          <div className="size-12 rounded-full bg-secondary flex items-center justify-center text-sm font-medium">{initials || "?"}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{c.full_name}</div>
          <div className="text-xs text-muted-foreground truncate">{c.city ?? "—"}{c.engagements_count > 0 && ` · ${c.engagements_count} engagemang`}</div>
        </div>
        {!c.active && <Badge variant="secondary">Inaktiv</Badge>}
        {!c.profile_completed_at && <Badge variant="outline">Inte ifylld</Badge>}
      </div>
      {c.bio && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{c.bio}</p>}
      <div className="flex flex-wrap gap-1">
        {[...c.industries.slice(0, 3), ...c.functions.slice(0, 3)].map((t) => (
          <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{t}</span>
        ))}
      </div>
    </Link>
  );
}

function CoachListRow({ c }: { c: CoachRow }) {
  const initials = c.full_name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  const tags = [...c.industries.slice(0, 2), ...c.functions.slice(0, 2)];
  return (
    <Link to="/coacher/$id" params={{ id: c.id }} className="flex items-center gap-4 px-5 py-3 hover:bg-secondary/40 transition-colors">
      {c.photo_url ? (
        <img src={c.photo_url} alt={c.full_name} className="size-10 rounded-full object-cover shrink-0" />
      ) : (
        <div className="size-10 rounded-full bg-secondary flex items-center justify-center text-xs font-medium shrink-0">{initials || "?"}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-medium truncate">{c.full_name}</div>
          {!c.active && <Badge variant="secondary" className="text-[10px]">Inaktiv</Badge>}
          {!c.profile_completed_at && <Badge variant="outline" className="text-[10px]">Inte ifylld</Badge>}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {c.city ?? "—"}{c.engagements_count > 0 && ` · ${c.engagements_count} engagemang`}
        </div>
      </div>
      <div className="hidden md:flex flex-wrap gap-1 max-w-[40%] justify-end">
        {tags.map((t) => (
          <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{t}</span>
        ))}
      </div>
    </Link>
  );
}
